import {
  Check,
  ChevronDown,
  FolderOpen,
  RefreshCw,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CreateSessionInput } from "@orni/domain-typescript";
import { sortProviderHistoryConversations } from "@orni/sdk-typescript";
import type { Session, SessionManagementMode } from "../domain";
import type {
  ConfigurableProviderId,
  PermissionMode,
  ProviderHistoryImportInput,
  ProviderHistoryCatalog,
  ProviderHistoryQuery,
  ProviderConversationSummary,
} from "../sdk/types";
import { useI18n } from "../i18n";
import type { TranslationKey } from "../locales/en";
import { ProviderIcon } from "./ProviderIcon";

interface SessionCreateDialogProps {
  initialMode: SessionManagementMode;
  initialProvider?: ConfigurableProviderId;
  enabledProviders: ConfigurableProviderId[];
  providerDisplayNames?: Partial<Record<ConfigurableProviderId, string>>;
  source?: "new" | "import";
  /** Agent management may import a batch; home/session entry remains single-select. */
  allowMultipleImports?: boolean;
  /** When opened from an Agent row, do not mix another CLI into the query. */
  historyProviderLocked?: boolean;
  initialTitle?: string;
  sessionRoot: string;
  initialPermissionMode?: PermissionMode;
  onClose: () => void;
  onOpenImport?: () => void;
  onCreate: (input: CreateSessionInput) => Promise<void>;
  onPickDirectory: () => Promise<string | null>;
  onDiscoverProviderHistory: (provider?: ConfigurableProviderId, options?: ProviderHistoryQuery) => Promise<ProviderHistoryCatalog>;
  onImportProviderHistory: (
    input: ProviderHistoryImportInput[],
    importedSessionId?: string,
  ) => Promise<void>;
  /** Report background import lifecycle after the dialog closes. */
  onImportStatus?: (status: {
    state: "accepted" | "completed" | "failed";
    count: number;
    provider: ConfigurableProviderId;
  }) => void;
  onOpenImportedSession?: (sessionId: string) => void;
}

const permissionDefinitions: { id: PermissionMode; title: TranslationKey; detail: TranslationKey }[] = [
  { id: "read-only", title: "create.permission.readOnly", detail: "create.permission.readOnlyDetail" },
  { id: "workspace-write", title: "create.permission.workspaceWrite", detail: "create.permission.workspaceWriteDetail" },
  { id: "full-access", title: "create.permission.fullAccess", detail: "create.permission.fullAccessDetail" },
];
export function SessionCreateDialog({
  initialMode,
  initialProvider,
  enabledProviders,
  providerDisplayNames = {},
  source = "new",
  allowMultipleImports = false,
  historyProviderLocked = false,
  initialTitle = "",
  sessionRoot,
  initialPermissionMode = "workspace-write",
  onClose,
  onOpenImport,
  onCreate,
  onPickDirectory,
  onDiscoverProviderHistory,
  onImportProviderHistory,
  onImportStatus,
  onOpenImportedSession,
}: SessionCreateDialogProps) {
  const { t } = useI18n();
  const permissions = useMemo(
    () => permissionDefinitions.map((item) => ({
      ...item,
      title: t(item.title),
      detail: t(item.detail),
    })),
    [t],
  );
  const importEntry = source === "import";
  const [step] = useState<"details" | "sources">(
    importEntry ? "sources" : "details",
  );
  const [title, setTitle] = useState(initialTitle);
  const [provider, setProvider] = useState<Session["provider"]>(
    initialProvider
      ? providerLabel(initialProvider)
      : providerLabel(
          enabledProviders.includes("claude")
            ? "claude"
            : (enabledProviders[0] ?? "claude"),
        ),
  );
  const mode: SessionManagementMode = "native";
  const enabledForMode = enabledProviders;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [permissionMode, setPermissionMode] =
    useState<PermissionMode>(initialPermissionMode);
  const [rootDraft, setRootDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyLoadedProviders, setHistoryLoadedProviders] = useState<Set<ConfigurableProviderId>>(new Set());
  // Keep the server-reported total separate from the rows hydrated in this
  // dialog. History discovery is paged, so using `conversations.length` here
  // would make a 600-row CLI look like it only has the first 100 rows.
  const [historyProviderCounts, setHistoryProviderCounts] = useState<Partial<Record<ConfigurableProviderId, number>>>({});
  const [historyError, setHistoryError] = useState("");
  const [conversations, setConversations] = useState<
    ProviderConversationSummary[]
  >([]);
  const [historyProvider, setHistoryProvider] = useState<ConfigurableProviderId>(
    initialProvider ?? (enabledProviders.includes("codex") ? "codex" : enabledProviders[0]) ?? "codex",
  );
  const [historyFilter, setHistoryFilter] = useState<
    "all" | "not-imported" | "imported"
  >("not-imported");
  const [historyNextCursor, setHistoryNextCursor] = useState<number | null>(null);
  const [historyNextCursorToken, setHistoryNextCursorToken] = useState<string | null>(null);
  const [historyComplete, setHistoryComplete] = useState(true);
  const [selectedConversations, setSelectedConversations] = useState<
    Set<string>
  >(new Set());
  const titleRef = useRef<HTMLInputElement>(null);
  const historyRequests = useRef(new Set<ConfigurableProviderId>());
  const historyCursorRestarts = useRef(new Map<ConfigurableProviderId, number>());
  const normalizedRoot = rootDraft.trim().replace(/\/$/, "");
  const visibleImportConversations = useMemo(
    () =>
      conversations.filter((conversation) => {
        if (conversation.provider !== historyProvider) return false;
        if (historyFilter === "imported") return Boolean(conversation.importedSessionId);
        if (historyFilter === "not-imported") return !conversation.importedSessionId;
        return true;
      }),
    [conversations, historyFilter, historyProvider],
  );
  const importConversationGroups = useMemo(
    () =>
      groupImportConversations(
        visibleImportConversations,
        t("create.unknownWorkspace"),
      ),
    [t, visibleImportConversations],
  );
  const importTitle =
    normalizedRoot.split("/").filter(Boolean).at(-1) ?? t("create.importedProject");
  const canContinue = importEntry
    ? true
    : Boolean(
        (normalizedRoot || sessionRoot) &&
          enabledForMode.includes(providerId(provider)),
      );
  const createDialogTitle = t(
    mode === "native" ? "create.title.native" : "create.title.orni",
    { provider },
  );

  useEffect(() => {
    if (!importEntry) titleRef.current?.focus();
  }, [importEntry]);
  useEffect(() => {
    if (enabledForMode.includes(providerId(provider)))
      return;
    if (enabledForMode.length)
      setProvider(
        providerLabel(
          enabledForMode.includes("claude") ? "claude" : enabledForMode[0],
        ),
      );
  }, [enabledForMode, provider]);
  useEffect(() => {
    if (!importEntry) return;
    void discoverSources();
    // Discovery is intentionally tied to opening the import surface.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importEntry]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const chooseRoot = async () => {
    const path = await onPickDirectory();
    if (!path) return;
    setRootDraft(path);
    if (importEntry && !title.trim())
      setTitle(path.split("/").filter(Boolean).at(-1) ?? t("create.importedProject"));
  };
  const discoverSources = async (
    requestedProvider = historyProvider,
    options?: ProviderHistoryQuery,
    cursorRestart = false,
  ) => {
    if (historyRequests.current.has(requestedProvider)) return;
    if (options?.cursor === undefined && !cursorRestart) {
      historyCursorRestarts.current.delete(requestedProvider);
    }
    historyRequests.current.add(requestedProvider);
    setHistoryBusy(true);
    setHistoryError("");
    try {
      const result = await onDiscoverProviderHistory(requestedProvider, {
        limit: 100,
        ...(options?.cursor !== undefined ? { cursor: options.cursor } : {}),
        ...(options?.cursorToken ? { cursorToken: options.cursorToken } : {}),
        ...(options?.refresh ? { refresh: true } : {}),
      });
      const providerResult = result.providers.find((item) => item.provider === requestedProvider);
      if (providerResult?.error) throw new Error(providerResult.error);
      if (providerResult) {
        setHistoryProviderCounts((current) => ({
          ...current,
          [requestedProvider]: providerResult.conversationCount,
        }));
      }
      const items = result.providers.filter((provider) => provider.provider === requestedProvider).flatMap((provider) =>
        provider.workspaces.flatMap((workspace) => workspace.conversations),
      );
      setConversations((current) => {
        const previous = options?.cursor !== undefined
          ? current
          : current.filter((conversation) => conversation.provider !== requestedProvider);
        return sortProviderHistoryConversations([...previous, ...items]);
      });
      setHistoryNextCursor(result.nextCursor ?? null);
      setHistoryNextCursorToken(result.nextCursorToken ?? null);
      setHistoryComplete(result.complete ?? result.nextCursor == null);
      setHistoryLoadedProviders((current) => new Set([...current, requestedProvider]));
      if (options?.cursor === undefined) setSelectedConversations(new Set());
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      const restarts = historyCursorRestarts.current.get(requestedProvider) ?? 0;
      if (options?.cursor !== undefined && isHistoryCursorRevisionMismatch(message) && restarts < 2) {
        historyCursorRestarts.current.set(requestedProvider, restarts + 1);
        window.setTimeout(() => void discoverSources(requestedProvider, undefined, true), 0);
        return;
      }
      setHistoryError(
        cause instanceof Error ? message : t("create.historyDiscoverError"),
      );
    } finally {
      historyRequests.current.delete(requestedProvider);
      setHistoryBusy(historyRequests.current.size > 0);
    }
  };
  const toggleConversation = (id: string) =>
    setSelectedConversations((current) => {
      const target = conversations.find((item) => item.sourceId === id);
      if (target?.importedSessionId) return current;
      if (!allowMultipleImports) {
        return current.has(id) ? new Set() : new Set([id]);
      }
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const submit = async () => {
    if (!canContinue || creating) return;
    setCreating(true);
    setHistoryError("");
    try {
      if (importEntry) {
        const selected = conversations
          .filter((conversation) =>
            selectedConversations.has(conversation.sourceId) &&
            !conversation.importedSessionId,
          )
          .map((conversation) => ({
            provider: conversation.provider,
            sourceId: conversation.sourceId,
            conversationId: conversation.conversationId,
            workspacePath: conversation.workspacePath,
            ...(conversation.historyHomeDirectory
              ? { historyHomeDirectory: conversation.historyHomeDirectory }
              : {}),
            title: conversation.title.trim() || title.trim() || importTitle,
          }));
        if (!selected.length) {
          setCreating(false);
          return;
        }
        onClose();
        // Provider history import creates stopped Session shells and can keep
        // processing after the command is accepted. Close the dialog first so
        // a large Codex batch never strands the user behind a receipt wait.
        onImportStatus?.({ state: "accepted", count: selected.length, provider: historyProvider });
        void onImportProviderHistory(selected).catch((cause) => {
          onImportStatus?.({ state: "failed", count: selected.length, provider: historyProvider });
          throw cause;
        }).then(() => {
          onImportStatus?.({ state: "completed", count: selected.length, provider: historyProvider });
        }).catch(() => undefined);
        return;
      }
      await onCreate({
        title: title.trim(),
        provider,
        managementMode: mode,
        workspaceRoot: normalizedRoot || sessionRoot,
        workspaceMode: normalizedRoot ? "existing" : "session-id",
        permissionMode,
      });
    } catch (cause) {
      if (importEntry) {
        setHistoryError(
          cause instanceof Error ? cause.message : t("create.historyError"),
        );
      }
      setCreating(false);
    }
  };

  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`session-create-dialog compact-session-dialog desktop-session-create-dialog ${importEntry ? "import-project-dialog" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-session-title"
      >
        <header>
          <div>
            <h2 id="create-session-title">
              {importEntry
                ? t("create.import.sessionsTitle")
                : createDialogTitle}
            </h2>
            <p>{importEntry ? t("create.import.sessionsLead") : t("create.nativeLead")}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <X size={16} />
          </button>
        </header>
        {step === "details" ? (
          <div className={`dialog-body ${importEntry ? "" : "compact-create-body"}`}>
            <label className={`dialog-field ${importEntry ? "" : "session-title-field"}`}>
              {importEntry ? <span>{t("create.projectName")}</span> : null}
              <input
                ref={titleRef}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    importEntry ? void discoverSources() : void submit();
                  }
                }}
                placeholder={
                  importEntry ? t("create.projectNameAuto") : t("create.sessionNamePlaceholder")
                }
                aria-label={importEntry ? t("create.projectName") : t("create.sessionName")}
              />
            </label>
            {importEntry ? (
              <fieldset className="dialog-field import-source-field">
                <legend>{t("create.projectDirectory")}</legend>
                <div className="path-input">
                  <input
                    value={rootDraft}
                    onChange={(event) => setRootDraft(event.target.value)}
                    aria-label={t("create.projectDirectoryAria")}
                  />
                  <button type="button" onClick={() => void chooseRoot()}>
                    <FolderOpen size={14} />
                    {t("create.chooseDirectory")}
                  </button>
                </div>
              </fieldset>
            ) : null}
            {!initialProvider && !importEntry ? (
              <div
                className={`dialog-field ${importEntry ? "" : "compact-choice-field"}`}
                role="group"
                aria-label={importEntry ? t("common.provider") : t("common.agent")}
              >
                <span className={importEntry ? "dialog-group-label" : "compact-choice-label"}>
                  {importEntry ? t("common.provider") : t("common.agent")}
                </span>
                <div className="provider-choice">
                  {enabledForMode.map((providerIdValue) => {
                    const item = providerLabel(providerIdValue);
                    const displayName = providerDisplayNames[providerIdValue] || item;
                    return <button
                      className={provider === item ? "selected" : ""}
                      type="button"
                      key={providerIdValue}
                      onClick={() => setProvider(item)}
                    >
                      <ProviderIcon provider={providerIdValue} size={14} />
                      {displayName}
                    </button>;
                  })}
                  {!enabledForMode.length ? (
                    <small className="dialog-provider-empty">
                      {t("common.providerRequired")}
                    </small>
                  ) : null}
                </div>
              </div>
            ) : null}
            {!importEntry ? <button
              className={`advanced-toggle ${advancedOpen ? "open" : ""}`}
              type="button"
              onClick={() => setAdvancedOpen((value) => !value)}
              aria-expanded={advancedOpen}
            >
              <SlidersHorizontal size={14} />
              <span>
                <strong>{t("create.advanced")}</strong>
                <small>
                  {
                    permissions.find((item) => item.id === permissionMode)
                      ?.title
                  }
                  {!importEntry ? ` · ${normalizedRoot || t("create.autoGenerated")}` : ""}
                </small>
              </span>
              <ChevronDown size={14} />
            </button> : null}
            {!importEntry && advancedOpen ? (
              <div className="advanced-session-settings">
                <div className="compact-setting-row workspace-field">
                  <span>{t("create.workspace")}</span>
                  <div className="compact-path-input">
                    <input
                      value={rootDraft}
                      onChange={(event) => setRootDraft(event.target.value)}
                      placeholder={t("create.workspacePlaceholder")}
                      aria-label={t("create.workspaceAria")}
                    />
                    <button
                      type="button"
                      onClick={() => void chooseRoot()}
                      title={t("create.chooseWorkspace")}
                      aria-label={t("create.chooseWorkspace")}
                    >
                      <FolderOpen size={14} />
                    </button>
                  </div>
                </div>
                <div className="compact-setting-row permission-field">
                  <span>{t("create.permissions")}</span>
                  <div className="permission-choice">
                    {permissions.map((item) => (
                      <button
                        className={permissionMode === item.id ? "selected" : ""}
                        type="button"
                        key={item.id}
                        onClick={() => setPermissionMode(item.id)}
                        title={item.detail}
                      >
                        <span className="permission-radio">
                          {permissionMode === item.id ? <i /> : null}
                        </span>
                        <span>
                          <strong>{item.title}</strong>
                          <small>{item.detail}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            {historyError ? (
              <p className="dialog-error">{historyError}</p>
            ) : null}
          </div>
        ) : (
          <div className="dialog-body import-selection-body">
            <section className="import-conversation-picker">
              {!historyProviderLocked ? <div
                className="import-provider-tabs"
                role="group"
                aria-label={t("common.provider")}
              >
                {(["codex", "claude", "opencode"] as const).map((item) => (
                  <button
                    type="button"
                    className={historyProvider === item ? "active" : ""}
                    key={item}
                    onClick={() => {
                      setHistoryProvider(item);
                      setHistoryNextCursor(null);
                      setHistoryComplete(true);
                      setSelectedConversations(new Set());
                      if (!historyLoadedProviders.has(item)) void discoverSources(item);
                    }}
                  >
                    <ProviderIcon provider={item} size={14} />
                    {providerLabel(item)}
                    <small>
                      {historyProviderCounts[item] ?? conversations.filter((conversation) => conversation.provider === item).length}
                    </small>
                  </button>
                ))}
              </div> : <div className="import-provider-scope" role="status">
                <ProviderIcon provider={historyProvider} size={14} />
                <strong>{providerLabel(historyProvider)}</strong>
                <small>{t("create.history.providerScoped")}</small>
              </div>}
              <div className="import-status-tabs" role="group" aria-label={t("create.history.filter")}>
                {(["not-imported", "all", "imported"] as const).map((item) => (
                  <button
                    type="button"
                    className={historyFilter === item ? "active" : ""}
                    key={item}
                    onClick={() => {
                      setHistoryFilter(item);
                      setSelectedConversations(new Set());
                    }}
                  >
                    {t(
                      item === "not-imported"
                        ? "create.history.notImported"
                        : item === "imported"
                          ? "create.history.imported"
                          : "common.all",
                    )}
                  </button>
                ))}
                <button
                  className="import-history-refresh"
                  type="button"
                  disabled={historyBusy}
                  onClick={() => {
                    setHistoryNextCursor(null);
                    setHistoryNextCursorToken(null);
                    setHistoryComplete(true);
                    void discoverSources(historyProvider, { refresh: true });
                  }}
                  title={t("create.history.refresh")}
                  aria-label={t("create.history.refresh")}
                >
                  <RefreshCw className={historyBusy ? "spin" : ""} size={13} />
                </button>
              </div>
              <header>
                <div>
                  <strong>{t("create.history.title")}</strong>
                  <small>
                    {t("create.history.selected", { selected: selectedConversations.size, total: visibleImportConversations.length })}
                  </small>
                </div>
                {allowMultipleImports && visibleImportConversations.some((item) => !item.importedSessionId) ? (
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedConversations((current) =>
                        current.size ===
                          visibleImportConversations.filter((item) => !item.importedSessionId).length
                          ? new Set()
                          : new Set(
                              visibleImportConversations
                                .filter((item) => !item.importedSessionId)
                                .map((item) => item.sourceId),
                            ),
                      )
                    }
                  >
                    {selectedConversations.size ===
                    visibleImportConversations.filter((item) => !item.importedSessionId).length
                      ? t("common.clearAll")
                      : t("common.selectAll")}
                  </button>
                ) : null}
              </header>
              <div className="import-conversation-groups">
                {historyBusy && !conversations.length ? (
                  <div className="import-conversation-empty">
                    <strong>{t("create.history.loading")}</strong>
                  </div>
                ) : visibleImportConversations.length ? (
                  importConversationGroups.flatMap((group) =>
                    group.workspaces.map((workspace) => (
                      <ImportConversationWorkspace
                        key={`${group.provider}:${workspace.path}`}
                        path={workspace.path}
                        conversations={workspace.conversations}
                        selected={selectedConversations}
                        onToggle={toggleConversation}
                        onOpenImported={onOpenImportedSession}
                      />
                    )),
                  )
                ) : (
                  <div className="import-conversation-empty">
                    <strong>
                      {historyFilter === "imported"
                        ? t("create.history.noImported")
                        : t("create.history.emptyTitle")}
                    </strong>
                    <span>{t("create.history.emptyDetail")}</span>
                  </div>
                )}
              </div>
              {historyNextCursor !== null && !historyComplete && !historyBusy ? (
                <button
                  className="import-history-load-more"
                  type="button"
                  onClick={() => void discoverSources(historyProvider, {
                    cursor: historyNextCursor,
                    ...(historyNextCursorToken ? { cursorToken: historyNextCursorToken } : {}),
                  })}
                >
                  {t("common.loadMore")}
                </button>
              ) : null}
            </section>
          </div>
        )}
        <footer>
          <div className="desktop-session-create-secondary-actions">
            <button className="ghost-button" type="button" onClick={onClose}>
              {t("common.cancel")}
            </button>
            {!importEntry && onOpenImport ? <button className="ghost-button desktop-session-import-button" type="button" onClick={onOpenImport} disabled={creating}>
              <FolderOpen size={14} />{t("sessions.importSession")}
            </button> : null}
          </div>
          <button
            className="primary-button"
            type="button"
            disabled={
              // Import is intentionally available as soon as at least one
              // loaded conversation is selected. History discovery can keep
              // paging in the background; waiting for its final page made a
              // large CLI history catalog look stuck and blocked imports that
              // were already ready to dispatch.
              !canContinue || creating ||
              (importEntry && selectedConversations.size === 0)
            }
            onClick={() => void submit()}
          >
            {creating
              ? t("create.creating")
              : !importEntry && historyBusy
                ? t("create.history.loading")
                : importEntry
                    ? t("create.importAndOpen")
                    : t("create.createAndOpen")}
          </button>
        </footer>
      </section>
    </div>
  );
}

function isHistoryCursorRevisionMismatch(message: string): boolean {
  return message.toLowerCase().includes("history cursor revision mismatch");
}

interface ImportConversationProvider {
  provider: ProviderConversationSummary["provider"];
  workspaces: {
    path: string;
    conversations: ProviderConversationSummary[];
  }[];
  conversationCount: number;
}

function ImportConversationWorkspace({
  path,
  conversations,
  selected,
  onToggle,
  onOpenImported,
}: {
  path: string;
  conversations: ProviderConversationSummary[];
  selected: Set<string>;
  onToggle: (sourceId: string) => void;
  onOpenImported?: (sessionId: string) => void;
}) {
  const { formatDateTime, t } = useI18n();
  const [expanded, setExpanded] = useState(conversations.length <= 5);
  const name = path.split("/").filter(Boolean).at(-1) ?? path;
  return (
    <section
      className={`import-workspace-group ${expanded ? "expanded" : "collapsed"}`}
    >
      <button
        className="import-workspace-heading"
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <FolderOpen size={13} />
        <span>
          <strong>{name}</strong>
            <small title={path}>{compactHistoryPath(path)}</small>
        </span>
        <b>{conversations.length}</b>
        <ChevronDown size={13} />
      </button>
      {expanded ? (
        <div>
          {conversations.map((conversation) => (
            <article
              className={[
                selected.has(conversation.sourceId) ? "selected" : "",
                conversation.importedSessionId ? "already-imported" : "",
              ].filter(Boolean).join(" ")}
              key={conversation.sourceId}
            >
              <button
                className="import-conversation-check"
                type="button"
                onClick={() =>
                  conversation.importedSessionId
                    ? onOpenImported?.(conversation.importedSessionId)
                    : onToggle(conversation.sourceId)
                }
                aria-label={
                  conversation.importedSessionId
                    ? t("common.view")
                    : selected.has(conversation.sourceId)
                    ? t("create.history.deselect")
                    : t("create.history.select")
                }
              >
                {conversation.importedSessionId ? (
                  <Check size={13} />
                ) : selected.has(conversation.sourceId) ? (
                  <Check size={13} />
                ) : null}
              </button>
              <button
                className="import-conversation-main"
                type="button"
                onClick={() =>
                  conversation.importedSessionId
                    ? onOpenImported?.(conversation.importedSessionId)
                    : onToggle(conversation.sourceId)
                }
              >
                <strong>
                  {conversation.title}
                  {conversation.importedSessionId ? (
                    <b className="imported-session-badge">
                      {t("create.history.imported")}
                    </b>
                  ) : null}
                </strong>
                <span>{historyWorkspaceName(conversation.workspacePath)}</span>
                <small>
                  {compactHistoryPath(conversation.workspacePath)} ·{" "}
                  {conversation.updatedAt
                    ? formatDateTime(conversation.updatedAt)
                    : t("common.unknownTime")}
                </small>
              </button>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function groupImportConversations(
  conversations: ProviderConversationSummary[],
  unknownWorkspace: string,
): ImportConversationProvider[] {
  const providerOrder: ProviderConversationSummary["provider"][] = [
    "codex",
    "claude",
    "opencode",
  ];
  return providerOrder.flatMap((provider) => {
    const providerConversations = sortProviderHistoryConversations(conversations.filter(
      (conversation) => conversation.provider === provider,
    ));
    if (!providerConversations.length) return [];
    const byWorkspace = new Map<string, ProviderConversationSummary[]>();
    for (const conversation of providerConversations) {
      const path = conversation.workspacePath || unknownWorkspace;
      const current = byWorkspace.get(path) ?? [];
      current.push(conversation);
      byWorkspace.set(path, current);
    }
    return [
      {
        provider,
        conversationCount: providerConversations.length,
        workspaces: [...byWorkspace.entries()].map(([path, items]) => ({
          path,
          conversations: sortProviderHistoryConversations(items),
        })).sort((left, right) => historyListTime(right.conversations) - historyListTime(left.conversations)),
      },
    ];
  });
}

function historyWorkspaceName(path: string): string {
  const parts = path.split(/[\\/]/).filter(Boolean);
  return parts.at(-1) ?? path;
}

function compactHistoryPath(path: string, maxLength = 64): string {
  if (path.length <= maxLength) return path;
  return `…${path.slice(-(maxLength - 1))}`;
}

function historyListTime(conversations: readonly ProviderConversationSummary[]): number {
  return conversations.reduce((latest, conversation) => {
    const value = Date.parse(conversation.updatedAt ?? conversation.startedAt ?? "");
    return Number.isFinite(value) ? Math.max(latest, value) : latest;
  }, Number.NEGATIVE_INFINITY);
}

function providerLabel(provider: ConfigurableProviderId): Session["provider"] {
  if (provider === "claude") return "Claude Code";
  if (provider === "opencode") return "OpenCode";
  return "Codex";
}

function providerId(provider: Session["provider"]): ConfigurableProviderId {
  if (provider === "Claude Code") return "claude";
  if (provider === "OpenCode") return "opencode";
  return "codex";
}
